import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { dashboardAPI, applicationsAPI, companiesAPI, jobsAPI } from '../../services/api';
import { formatSalary, timeAgo, APPLICATION_STATUSES } from '../../utils/helpers';
import { FiBriefcase, FiUsers, FiEye, FiCalendar, FiTrendingUp, FiPlus, FiMapPin, FiArrowRight, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import toast from 'react-hot-toast';
import './Dashboard.css';

const StatusBadge = ({ status }) => {
  const s = APPLICATION_STATUSES[status] || APPLICATION_STATUSES.applied;
  return <span className="badge" style={{ background: s.bg, color: s.color }}>{s.label}</span>;
};

const StatCard = ({ icon, value, label, color }) => (
  <div className="card dash-stat-card">
    <div className="dash-stat-icon" style={{ color, background: `${color}15` }}>{icon}</div>
    <div className="dash-stat-value">{value}</div>
    <div className="dash-stat-label">{label}</div>
  </div>
);

const Dashboard = () => {
  const { user, isRecruiter, isAdmin } = useAuth();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const recruiterView = isRecruiter || isAdmin;

  // Company creation state
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [companyFormData, setCompanyFormData] = useState({
    name: '', description: '', industry: '', website: '', location: '', size: '', email: '', phone: '',
  });
  const [savingCompany, setSavingCompany] = useState(false);

  // Recruiter: separate state for posted jobs and received applications
  const [myJobs, setMyJobs] = useState([]);
  const [receivedApps, setReceivedApps] = useState([]);

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      if (recruiterView) {
        // Fetch recruiter dashboard
        try {
          const { data: res } = await dashboardAPI.recruiter();
          const d = res.data || res;
          setData(d);
          setMyJobs(d.jobs || d.recent_jobs || d.posted_jobs || []);
          setReceivedApps(d.applications || d.recent_applications || []);
        } catch (err) {
          setData({});
        }

        // Also fetch the recruiter's own posted jobs directly
        try {
          const { data: jobsRes } = await jobsAPI.list({ posted_by: 'me' });
          const fetchedJobs = jobsRes.data || jobsRes.jobs || jobsRes.items || (Array.isArray(jobsRes) ? jobsRes : []);
          if (fetchedJobs.length > 0) setMyJobs(fetchedJobs);
        } catch (e) { /* keep dashboard data */ }

        // Fetch applications received
        try {
          const { data: appsRes } = await applicationsAPI.list({ role: 'recruiter' });
          const fetchedApps = appsRes.data || appsRes.applications || appsRes.items || (Array.isArray(appsRes) ? appsRes : []);
          if (fetchedApps.length > 0) setReceivedApps(fetchedApps);
        } catch (e) { /* keep dashboard data */ }
      } else {
        // Candidate dashboard
        try {
          const { data: res } = await dashboardAPI.candidate();
          setData(res.data || res);
        } catch (err) {
          setData({});
        }
      }
    } catch (err) {
      setData({});
    }
    setLoading(false);
  };

  const updateStatus = async (appId, status) => {
    try {
      await applicationsAPI.updateStatus(appId, { status });
      toast.success(`Status updated to ${status}`);
      loadDashboard();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    if (!companyFormData.name.trim()) { toast.error('Company name is required'); return; }
    setSavingCompany(true);
    try {
      // Only send non-empty fields to avoid backend validation errors
      const payload = {};
      Object.entries(companyFormData).forEach(([key, val]) => {
        if (val && String(val).trim()) payload[key] = String(val).trim();
      });
      await companiesAPI.create(payload);
      toast.success('Company created successfully!');
      setShowCompanyForm(false);
      setCompanyFormData({ name: '', description: '', industry: '', website: '', location: '', size: '', email: '', phone: '' });
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.error || 'Failed to create company');
    }
    setSavingCompany(false);
  };

  const hasCompany = data?.company || user?.company_id || user?.company;

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  const candidateApps = data?.recent_applications || data?.applications || [];
  const matches = data?.matched_jobs || data?.matching_jobs || [];

  // =========================================================
  // RECRUITER / ADMIN DASHBOARD
  // =========================================================
  if (recruiterView) {
    return (
      <div className="dashboard-page">
        <div className="container">
          <div className="dashboard-header">
            <div>
              <h1>Welcome back, {user?.first_name || 'there'}</h1>
              <p>Here's what's happening with your recruitment pipeline</p>
            </div>
            <Link to="/jobs/create" className="btn btn-primary"><FiPlus /> Post New Job</Link>
          </div>

          {/* Company creation banner for recruiter + admin */}
          {!hasCompany && (
            <div className="card" style={{ marginBottom: '1.5rem', padding: '1.25rem', borderLeft: '3px solid var(--accent-primary, #3b82f6)' }}>
              {!showCompanyForm ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
                  <div>
                    <strong style={{ fontSize: '0.95rem' }}>No company profile yet</strong>
                    <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', opacity: 0.7 }}>Add your company to start posting jobs.</p>
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={() => setShowCompanyForm(true)}><FiPlus /> Add Company</button>
                </div>
              ) : (
                <form onSubmit={handleCreateCompany}>
                  <strong style={{ display: 'block', marginBottom: '0.75rem' }}>Create Company</strong>
                  <div className="grid-2" style={{ marginBottom: '0.75rem' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Company Name *</label>
                      <input type="text" placeholder="Acme Pty Ltd" value={companyFormData.name} onChange={(e) => setCompanyFormData({ ...companyFormData, name: e.target.value })} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Industry</label>
                      <select value={companyFormData.industry} onChange={(e) => setCompanyFormData({ ...companyFormData, industry: e.target.value })}>
                        <option value="">Select</option>
                        {['Technology','Finance','Healthcare','Education','Manufacturing','Retail','Construction','Mining','Agriculture','Telecommunications','Legal','Marketing','Consulting','Other'].map(i => <option key={i} value={i.toLowerCase()}>{i}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="form-group" style={{ margin: '0 0 0.75rem' }}>
                    <label>Description</label>
                    <textarea rows="2" placeholder="Brief description..." value={companyFormData.description} onChange={(e) => setCompanyFormData({ ...companyFormData, description: e.target.value })} />
                  </div>
                  <div className="grid-2" style={{ marginBottom: '0.75rem' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Website</label>
                      <input type="url" placeholder="https://company.co.za" value={companyFormData.website} onChange={(e) => setCompanyFormData({ ...companyFormData, website: e.target.value })} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Location</label>
                      <input type="text" placeholder="Johannesburg, Gauteng" value={companyFormData.location} onChange={(e) => setCompanyFormData({ ...companyFormData, location: e.target.value })} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={savingCompany}>{savingCompany ? 'Creating...' : 'Create Company'}</button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowCompanyForm(false)}>Cancel</button>
                  </div>
                </form>
              )}
            </div>
          )}

          <div className="dashboard-stats">
            <StatCard icon={<FiBriefcase />} value={data?.active_jobs || data?.jobs_count || myJobs.length || 0} label="Active Jobs" color="var(--accent-primary)" />
            <StatCard icon={<FiUsers />} value={data?.total_applicants || data?.applications_count || receivedApps.length || 0} label="Applicants" color="var(--accent-secondary)" />
            <StatCard icon={<FiEye />} value={data?.interviews_scheduled || 0} label="Interviews" color="var(--accent-purple)" />
            <StatCard icon={<FiCheckCircle />} value={data?.offers_made || 0} label="Offers" color="var(--accent-success)" />
          </div>

          <div className="tabs">
            <button className={`tab ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>Overview</button>
            <button className={`tab ${tab === 'jobs' ? 'active' : ''}`} onClick={() => setTab('jobs')}>My Posted Jobs</button>
            <button className={`tab ${tab === 'applicants' ? 'active' : ''}`} onClick={() => setTab('applicants')}>Applications Received</button>
          </div>

          <div className="dashboard-content">
            {tab === 'overview' && (
              <div className="overview-grid">
                <div className="card">
                  <div className="card-header-row">
                    <h3>Recent Applications</h3>
                    <button className="view-all" onClick={() => setTab('applicants')}>View all <FiArrowRight /></button>
                  </div>
                  {receivedApps.length > 0 ? (
                    <div className="app-list">
                      {receivedApps.slice(0, 5).map((app) => (
                        <div className="app-item" key={app.id}>
                          <div className="app-item-info">
                            <div className="app-item-name">{app.candidate_name || app.user_name || `${app.user?.first_name || ''} ${app.user?.last_name || ''}`.trim() || 'Candidate'}</div>
                            <div className="app-item-meta">{app.job_title || app.job?.title || app.job?.name || 'Job'} · {timeAgo(app.created_at)}</div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <StatusBadge status={app.status} />
                            {(app.status === 'applied' || app.status === 'pending') && (
                              <>
                                <button className="btn btn-sm btn-success" onClick={() => updateStatus(app.id, 'reviewing')}><FiCheckCircle /></button>
                                <button className="btn btn-sm btn-danger" onClick={() => updateStatus(app.id, 'rejected')}><FiXCircle /></button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : <div className="empty-state"><p>No applications received yet</p></div>}
                </div>

                <div className="card">
                  <div className="card-header-row">
                    <h3>My Posted Jobs</h3>
                    <button className="view-all" onClick={() => setTab('jobs')}>View all <FiArrowRight /></button>
                  </div>
                  {myJobs.length > 0 ? (
                    <div className="app-list">
                      {myJobs.slice(0, 5).map((job) => (
                        <Link to={`/jobs/${job.id}`} className="app-item" key={job.id}>
                          <div className="app-item-info">
                            <div className="app-item-name">{job.title || job.name}</div>
                            <div className="app-item-meta"><FiMapPin /> {job.location_name || job.location || 'SA'} · {job.applications_count || 0} applicants</div>
                          </div>
                          <span className={`badge ${job.is_active !== false ? 'badge-success' : ''}`}>{job.is_active !== false ? 'Active' : 'Closed'}</span>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p>No jobs posted yet</p>
                      <Link to="/jobs/create" className="btn btn-primary btn-sm"><FiPlus /> Post a Job</Link>
                    </div>
                  )}
                </div>
              </div>
            )}

            {tab === 'jobs' && (
              myJobs.length > 0 ? (
                <div className="table-wrapper">
                  <table>
                    <thead><tr><th>Title</th><th>Location</th><th>Type</th><th>Applicants</th><th>Status</th><th></th></tr></thead>
                    <tbody>{myJobs.map((j) => (
                      <tr key={j.id}>
                        <td><Link to={`/jobs/${j.id}`} style={{ fontWeight: 500 }}>{j.title || j.name}</Link></td>
                        <td>{j.location_name || j.location || '-'}</td>
                        <td><span className="badge">{j.job_type || 'Full-time'}</span></td>
                        <td>{j.applications_count || 0}</td>
                        <td><span className={`badge ${j.is_active !== false ? 'badge-success' : ''}`}>{j.is_active !== false ? 'Active' : 'Closed'}</span></td>
                        <td><Link to={`/jobs/${j.id}/edit`} className="btn btn-secondary btn-sm">Edit</Link></td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              ) : <div className="empty-state"><div className="icon">💼</div><h3>No jobs posted</h3><Link to="/jobs/create" className="btn btn-primary"><FiPlus /> Post Your First Job</Link></div>
            )}

            {tab === 'applicants' && (
              receivedApps.length > 0 ? (
                <div className="table-wrapper">
                  <table>
                    <thead><tr><th>Candidate</th><th>Job</th><th>Applied</th><th>Status</th><th>Actions</th></tr></thead>
                    <tbody>{receivedApps.map((a) => (
                      <tr key={a.id}>
                        <td style={{ fontWeight: 500 }}>{a.candidate_name || a.user_name || `${a.user?.first_name || ''} ${a.user?.last_name || ''}`.trim() || 'Candidate'}</td>
                        <td>{a.job_title || a.job?.title || a.job?.name || '-'}</td>
                        <td>{timeAgo(a.created_at)}</td>
                        <td><StatusBadge status={a.status} /></td>
                        <td>
                          <select value={a.status} onChange={(e) => updateStatus(a.id, e.target.value)} style={{ padding: '0.35rem 0.5rem', fontSize: '0.8rem', width: 'auto' }}>
                            {Object.entries(APPLICATION_STATUSES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              ) : <div className="empty-state"><div className="icon">👥</div><h3>No applicants yet</h3><p>Applicants will appear here when candidates apply to your jobs</p></div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // =========================================================
  // CANDIDATE DASHBOARD
  // =========================================================
  return (
    <div className="dashboard-page">
      <div className="container">
        <div className="dashboard-header">
          <div>
            <h1>Welcome back, {user?.first_name || 'there'}</h1>
            <p>Here's what's happening with your job search</p>
          </div>
        </div>

        <div className="dashboard-stats">
          <StatCard icon={<FiBriefcase />} value={data?.applications_count || 0} label="Applications" color="var(--accent-primary)" />
          <StatCard icon={<FiEye />} value={data?.profile_views || 0} label="Profile Views" color="var(--accent-secondary)" />
          <StatCard icon={<FiCalendar />} value={data?.interviews_count || 0} label="Interviews" color="var(--accent-purple)" />
          <StatCard icon={<FiTrendingUp />} value={data?.matched_jobs_count || matches.length || 0} label="Matches" color="var(--accent-success)" />
        </div>

        <div className="tabs">
          <button className={`tab ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>Overview</button>
          <button className={`tab ${tab === 'applications' ? 'active' : ''}`} onClick={() => setTab('applications')}>My Applications</button>
          <button className={`tab ${tab === 'matches' ? 'active' : ''}`} onClick={() => setTab('matches')}>Matches</button>
        </div>

        <div className="dashboard-content">
          {tab === 'overview' && (
            <div className="overview-grid">
              <div className="card">
                <div className="card-header-row">
                  <h3>My Applications</h3>
                  <button className="view-all" onClick={() => setTab('applications')}>View all <FiArrowRight /></button>
                </div>
                {candidateApps.length > 0 ? (
                  <div className="app-list">
                    {candidateApps.slice(0, 5).map((app) => (
                      <div className="app-item" key={app.id}>
                        <div className="app-item-info">
                          <div className="app-item-name">{app.job_title || app.job?.title || 'Job'}</div>
                          <div className="app-item-meta">{app.company_name || app.job?.company?.name || 'Company'} · {timeAgo(app.created_at)}</div>
                        </div>
                        <StatusBadge status={app.status} />
                      </div>
                    ))}
                  </div>
                ) : <div className="empty-state"><p>No applications yet</p><Link to="/jobs" className="btn btn-primary btn-sm">Browse Jobs</Link></div>}
              </div>

              <div className="card">
                <div className="card-header-row">
                  <h3>Recommended for You</h3>
                  <Link to="/jobs" className="view-all">View all <FiArrowRight /></Link>
                </div>
                {matches.length > 0 ? (
                  <div className="app-list">
                    {matches.slice(0, 5).map((job) => (
                      <Link to={`/jobs/${job.id}`} className="app-item" key={job.id}>
                        <div className="app-item-info">
                          <div className="app-item-name">{job.title || job.name}</div>
                          <div className="app-item-meta"><FiMapPin /> {job.location_name || job.location || 'SA'} · {formatSalary(job.salary_min, job.salary_max)}</div>
                        </div>
                        <span className="badge">{job.job_type || 'Full-time'}</span>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>No recommendations yet</p>
                    <Link to="/profile" className="btn btn-secondary btn-sm">Complete Profile</Link>
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === 'applications' && (
            candidateApps.length > 0 ? (
              <div className="table-wrapper">
                <table>
                  <thead><tr><th>Job</th><th>Company</th><th>Applied</th><th>Status</th><th></th></tr></thead>
                  <tbody>{candidateApps.map((a) => (
                    <tr key={a.id}>
                      <td><Link to={`/jobs/${a.job_id}`} style={{ fontWeight: 500 }}>{a.job_title || 'Job'}</Link></td>
                      <td>{a.company_name || '-'}</td>
                      <td>{timeAgo(a.created_at)}</td>
                      <td><StatusBadge status={a.status} /></td>
                      <td>{(a.status === 'applied' || a.status === 'pending') && <button className="btn btn-sm btn-danger" onClick={() => updateStatus(a.id, 'withdrawn')}>Withdraw</button>}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            ) : <div className="empty-state"><div className="icon">📄</div><h3>No applications yet</h3><Link to="/jobs" className="btn btn-primary">Browse Jobs</Link></div>
          )}

          {tab === 'matches' && (
            matches.length > 0 ? (
              <div className="jobs-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                {matches.map((job) => (
                  <Link to={`/jobs/${job.id}`} className="job-card" key={job.id}>
                    <h3 className="job-title">{job.title || job.name}</h3>
                    <div className="job-company-name">{job.company_name}</div>
                    <div className="job-location"><FiMapPin /> {job.location_name || 'SA'}</div>
                    <div className="job-card-footer">
                      <span className="badge">{job.job_type || 'Full-time'}</span>
                      <span className="job-salary">{formatSalary(job.salary_min, job.salary_max)}</span>
                    </div>
                    {job.match_score && <div className="match-score">{Math.round(job.match_score)}% match</div>}
                  </Link>
                ))}
              </div>
            ) : <div className="empty-state"><div className="icon">🎯</div><h3>No matches yet</h3><p>Complete your profile to get job recommendations</p><Link to="/profile" className="btn btn-primary">Update Profile</Link></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
